import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Trophy, Target, Medal, Handshake, Globe, Quote, ChevronLeft, ChevronRight, Images, GraduationCap, ScanSearch, Plane, Wallet, Star, MapPin, Rss, Calendar, Building2, Package, Tv2, CheckCircle } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCms } from "@/hooks/useCms";
import { FlagIcon } from "@/components/FlagIcon";
import { InteractiveWorldMap } from "@/components/InteractiveWorldMap";
import { db } from "@/lib/firebase";
import { doc, getDoc, getDocs, collection, query, where, orderBy, limit } from "firebase/firestore";

type FeaturedAthlete = { id: string; name: string; sport: string; nationality: string; position: string; image_url: string; achievements: string[]; is_featured: boolean };

const DEFAULT_GLOBAL_PRESENCE = [
  { flag: "🇩🇪", country: "Germany", addr: "Unterseilberg 9, 94143, Grainet, Bayern" },
  { flag: "🇺🇸", country: "USA", addr: "20270 Plymouth Rd, Detroit, MI 48228" },
  { flag: "🇨🇦", country: "Canada", addr: "1315 Finch Avenue West, Toronto ON M3J 2G6" },
  { flag: "🇬🇧", country: "UK", addr: "1A Town Square, Erith, Kent DA8 1RE" },
  { flag: "🇳🇬", country: "Nigeria", addr: "95 Ogudu Road, Ogudu, Lagos" },
];

const DEFAULT_CORE_VALUES = [
  { emoji: "🏆", name: "Excellence", desc: "Striving for the highest standard in athlete development." },
  { emoji: "🤝", name: "Integrity", desc: "Honest, fair, and transparent in all dealings." },
  { emoji: "🌍", name: "Global Vision", desc: "Thinking internationally, acting locally." },
  { emoji: "❤️", name: "Passion", desc: "Deep love for sports drives everything we do." },
];

const DEFAULT_HERO_SLIDES = [
  { img: "https://images.unsplash.com/photo-1551958219-acbc25b32f8e?w=1600&auto=format&fit=crop", headline: "DEVELOPING GLOBAL", highlight: "SPORTS TALENTS", sub: "The trusted platform connecting athletes with professional opportunities across Africa, Europe, and North America." },
  { img: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1600&auto=format&fit=crop", headline: "ELIADA SPORTS", highlight: "ACADEMY", sub: "World-class training programs developing technical skills, discipline, and tactical excellence in young athletes." },
  { img: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1600&auto=format&fit=crop", headline: "BUILDING CAREERS,", highlight: "SHAPING FUTURES", sub: "From contract negotiations to international transfers, we manage every step of your professional sports journey." },
];

const DEFAULT_TESTIMONIALS = [
  { quote: "Eliada Sports Agency got me my first transfer deal in Europe. Their expertise and management prowess are extremely appreciated.", author: "Ferdinand", role: "(Belgium)", avatar: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=120&h=120&auto=format&fit=crop&crop=face" },
  { quote: "Two years ago, I signed up with Eliada Sports Agency and they helped me secure the club I am playing for now.", author: "Melvin", role: "(UK)", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&auto=format&fit=crop&crop=face" },
  { quote: "It's been nice working with Eliada Sports. They are professional in their field and they have high level of integrity.", author: "Alina", role: "(Germany)", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&auto=format&fit=crop&crop=face" },
  { quote: "Eliada Sports Agency, you guys are simply amazing. Thank you for helping me seal my football career deal.", author: "Maria", role: "(Mexico)", avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=120&h=120&auto=format&fit=crop&crop=face" },
];

const DEFAULT_GALLERY_PREVIEW = [
  { src: "https://images.unsplash.com/photo-1551958219-acbc25b32f8e?w=800&auto=format&fit=crop", alt: "Football training session", label: "Academy" },
  { src: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&auto=format&fit=crop", alt: "Players on the pitch", label: "Academy" },
  { src: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=800&auto=format&fit=crop", alt: "Scouting tournament", label: "Scouting" },
  { src: "https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=800&auto=format&fit=crop", alt: "Youth talent showcase", label: "Events" },
  { src: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&auto=format&fit=crop", alt: "Player signing ceremony", label: "Transfers" },
  { src: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop", alt: "Skills training", label: "Academy" },
  { src: "https://images.unsplash.com/photo-1518604666860-9ed391f76460?w=800&auto=format&fit=crop", alt: "Community event", label: "Events" },
  { src: "https://images.unsplash.com/photo-1522778526097-ce0a22cdbec6?w=800&auto=format&fit=crop", alt: "Stadium atmosphere", label: "Events" },
];

const ICON_MAP: Record<string, React.ElementType> = {
  GraduationCap, ScanSearch, Plane, Wallet, Medal, Handshake, Globe, ShieldCheck, Trophy, Target
};

export default function Home() {
  const { getMeta, getText } = useCms();

  const { data: publicSettings = {} } = useQuery<Record<string, string>>({
    queryKey: ["/api/settings/site"],
    queryFn: async () => {
      try {
        const snap = await getDoc(doc(db, "configs", "generalSettings"));
        return snap.exists() ? (snap.data() as Record<string, string>) : {};
      } catch (err) {
        console.error("Failed to load settings from Firestore:", err);
        return {};
      }
    },
    staleTime: 1000 * 60 * 2,
  });

  const athletesSectionEnabled = publicSettings.athletes_section_enabled !== "false";

  const { data: featuredAthletes = [] } = useQuery<FeaturedAthlete[]>({
    queryKey: ["/api/athletes/featured"],
    queryFn: async () => {
      try {
        const q = query(
          collection(db, "athletes"),
          where("is_featured", "==", true),
          orderBy("display_order", "asc"),
          limit(6)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          let parsedAchievements: string[] = [];
          if (Array.isArray(item.achievements)) {
            parsedAchievements = item.achievements;
          } else if (typeof item.achievements === "string") {
            try {
              parsedAchievements = JSON.parse(item.achievements);
            } catch {
              parsedAchievements = [];
            }
          }
          return {
            id: d.id,
            name: item.name || "",
            sport: item.sport || "",
            nationality: item.nationality || "",
            position: item.position || "",
            image_url: item.image_url || "",
            achievements: parsedAchievements,
            is_featured: item.is_featured || false,
          };
        });
      } catch (err) {
        console.error("Failed to fetch featured athletes:", err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 2,
  });

  const showAthletesSection = athletesSectionEnabled && featuredAthletes.length > 0;

  type GalleryImage = { id: string; image_url: string; title: string; caption: string; category: string; display_order: number };
  const { data: galleryImages = [] } = useQuery<GalleryImage[]>({
    queryKey: ["/api/gallery"],
    queryFn: async () => {
      try {
        const q = query(collection(db, "gallery_images"), orderBy("display_order", "asc"));
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          return {
            id: d.id,
            image_url: item.image_url || "",
            title: item.title || "",
            caption: item.caption || "",
            category: item.category || "General",
            display_order: item.display_order || 0,
          };
        });
      } catch (err) {
        console.error("Failed to fetch gallery images:", err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  type HomeSection = { id: string; label: string; visible: boolean };
  const DEFAULT_HOME_SECTIONS: HomeSection[] = [
    { id: "hero", label: "Hero Slider", visible: true },
    { id: "feature_strip", label: "Feature Strip", visible: true },
    { id: "stats", label: "Stats Counter", visible: true },
    { id: "commitment", label: "Commitment Banner", visible: true },
    { id: "mission", label: "Mission Intro", visible: true },
    { id: "services_preview", label: "Services Preview", visible: true },
    { id: "why_choose", label: "Why Choose Us", visible: true },
    { id: "global_presence", label: "Global Presence", visible: true },
    { id: "core_values", label: "Core Values", visible: true },
    { id: "athletes", label: "Featured Athletes", visible: true },
    { id: "blog", label: "Latest News & Blog", visible: true },
    { id: "testimonials", label: "Testimonials", visible: true },
    { id: "gallery_preview", label: "Gallery Preview", visible: true },
    { id: "whatsapp", label: "WhatsApp Channel", visible: true },
    { id: "partner_preview", label: "Partner With Us", visible: true },
    { id: "cta", label: "Call to Action", visible: true },
  ];

  const homeSectionOrder = useMemo<HomeSection[]>(() => {
    if (!publicSettings.home_section_order) return DEFAULT_HOME_SECTIONS;
    try {
      const stored = JSON.parse(publicSettings.home_section_order) as HomeSection[];
      const storedIds = new Set(stored.map(s => s.id));
      const missing = DEFAULT_HOME_SECTIONS.filter(s => !storedIds.has(s.id));
      return [...stored, ...missing];
    }
    catch { return DEFAULT_HOME_SECTIONS; }
  }, [publicSettings.home_section_order]);

  const heroSlides = getMeta<{ slides: typeof DEFAULT_HERO_SLIDES }>("home_hero_slides", { slides: DEFAULT_HERO_SLIDES }).slides ?? DEFAULT_HERO_SLIDES;
  const testimonials = getMeta<{ items: typeof DEFAULT_TESTIMONIALS }>("home_testimonials", { items: DEFAULT_TESTIMONIALS }).items ?? DEFAULT_TESTIMONIALS;
  const galleryPreview = galleryImages.length > 0
    ? galleryImages.map(img => ({ src: img.image_url, alt: img.caption || img.title || "", label: img.category || "Gallery" }))
    : DEFAULT_GALLERY_PREVIEW;

  const featureStrip = getMeta<{ items: Array<{ icon: string; title: string; sub: string }> }>("home_feature_strip", {
    items: [
      { icon: "GraduationCap", title: "Eliada Football Academy", sub: "World-class training across Africa & Europe" },
      { icon: "ScanSearch", title: "International Scouting", sub: "Discovering raw talent in every corner of the globe" },
      { icon: "Plane", title: "Global Transfers", sub: "Connecting athletes to professional clubs worldwide" },
      { icon: "Wallet", title: "Career Investment", sub: "Building champions on and off the field" },
    ]
  }).items ?? [];

  const stats = getMeta<{ items: Array<{ num: string; label: string }> }>("home_stats", {
    items: [
      { num: "50+", label: "Athletes Represented" },
      { num: "5", label: "Global Offices" },
      { num: "3", label: "Continents Covered" },
      { num: "10+", label: "Years of Experience" },
    ]
  }).items ?? [];

  const commitmentMeta = getMeta<{ pillars: string[]; description: string }>("home_commitment", {
    pillars: ["DISCOVER TALENT", "DEVELOP ATHLETES", "CREATE OPPORTUNITIES", "BUILD CAREERS"],
    description: "Through professionalism, integrity, and passion for sports, the agency continues to shape the future of young athletes and contribute to the growth of the international sports industry.",
  });

  const missionText = getText("home_mission", "Founded with a vision to create a structured pathway for young athletes. We combine talent identification, professional training, mentorship, financial support, and international exposure.");
  const missionMeta = getMeta<{ image?: string; bullets?: string[]; headline?: string }>("home_mission", {
    image: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?q=80&w=2000&auto=format&fit=crop",
    bullets: ["Identify and nurture promising talents worldwide", "Connect athletes with international clubs & scouts", "Promote fair, ethical, and transparent management"],
    headline: "EMPOWERING ATHLETES TO REACH THEIR FULL POTENTIAL",
  });

  const servicesPreview = getMeta<{ items: Array<{ title: string; desc: string; href: string }> }>("home_services_preview", {
    items: [
      { title: "Football Academy", desc: "Structured training programs focusing on technical skills, discipline, and tactical coaching.", href: "/services/football-academy" },
      { title: "Talent Scouting", desc: "Active scouting networks across Africa and Europe evaluating promising players.", href: "/services/talent-scouting" },
      { title: "Player Representation", desc: "Professional management, contract negotiations, and career planning.", href: "/services/player-representation" },
    ]
  }).items ?? [];

  const whyChoose = getMeta<{ items: Array<{ icon: string; title: string; desc: string }> }>("home_why_choose", {
    items: [
      { icon: "Medal", title: "Proven Track Record", desc: "Decades of experience placing athletes in top clubs globally." },
      { icon: "Handshake", title: "Ethical Representation", desc: "Fair, transparent management with athlete interests first." },
      { icon: "Globe", title: "Global Network", desc: "5 offices across Europe, North America, and Africa." },
      { icon: "ShieldCheck", title: "Full-Service Support", desc: "From scouting through to visa, transfers, and career management." },
    ]
  }).items ?? [];

  const ctaMeta = getMeta<{ heading?: string; subtext?: string; buttonText?: string }>("home_cta", {
    heading: "READY TO START YOUR JOURNEY?",
    subtext: "Join hundreds of athletes who trust Eliada Sports Agency to manage their professional careers.",
    buttonText: "Join The Agency",
  });

  const partnerMeta = getMeta<{ heading?: string; subtext?: string; buttonText?: string }>("home_partner", {
    heading: "PARTNER WITH US",
    subtext: "Connect your brand with the world's most exciting sports talent pipeline. Together, we develop champions — and winning partnerships.",
    buttonText: "Explore All Partnerships",
  });
  const partnerPreviewEnabled = publicSettings.partner_preview_enabled !== "false";

  type BlogPost = { id: string; title: string; slug: string; excerpt: string; cover_image_url: string | null; published_at: string; };
  const { data: latestPosts = [] } = useQuery<BlogPost[]>({
    queryKey: ["/api/blog"],
    queryFn: async () => {
      try {
        const q = query(
          collection(db, "blog_posts"),
          where("status", "==", "published"),
          orderBy("published_at", "desc"),
          limit(3)
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          return {
            id: d.id,
            title: item.title || "",
            slug: item.slug || "",
            excerpt: item.excerpt || "",
            cover_image_url: item.cover_image_url || null,
            published_at: item.published_at || "",
          };
        });
      } catch (err) {
        console.error("Failed to fetch latest blog posts:", err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 2,
  });

  const globalPresence = getMeta<{ items: typeof DEFAULT_GLOBAL_PRESENCE }>("about_global_presence", { items: DEFAULT_GLOBAL_PRESENCE }).items ?? DEFAULT_GLOBAL_PRESENCE;
  const globalPresenceSubtitle = getText("about_global_presence", "We maintain active operations and partnerships across multiple continents to serve our athletes locally and globally.");
  const coreValues = getMeta<{ items: typeof DEFAULT_CORE_VALUES }>("about_core_values", { items: DEFAULT_CORE_VALUES }).items ?? DEFAULT_CORE_VALUES;
  const coreValuesHeadline = getText("about_core_values", "OUR CORE VALUES");

  // Hero slider
  const [heroRef, heroApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 5000 })]);
  const [heroIndex, setHeroIndex] = useState(0);
  const heroPrev = useCallback(() => { if (heroApi) heroApi.scrollPrev(); }, [heroApi]);
  const heroNext = useCallback(() => { if (heroApi) heroApi.scrollNext(); }, [heroApi]);
  const heroScrollTo = useCallback((i: number) => { if (heroApi) heroApi.scrollTo(i); }, [heroApi]);
  useEffect(() => {
    if (!heroApi) return;
    const onSelect = () => setHeroIndex(heroApi.selectedScrollSnap());
    onSelect();
    heroApi.on("select", onSelect);
    heroApi.on("reInit", onSelect);
    return () => { heroApi.off("select", onSelect); heroApi.off("reInit", onSelect); };
  }, [heroApi]);

  // Testimonials slider
  const [testimonialsRef, testimonialsApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 4500 })]);
  const testPrev = useCallback(() => { if (testimonialsApi) testimonialsApi.scrollPrev(); }, [testimonialsApi]);
  const testNext = useCallback(() => { if (testimonialsApi) testimonialsApi.scrollNext(); }, [testimonialsApi]);

  // Gallery slider
  const [galleryRef, galleryApi] = useEmblaCarousel({ loop: true, align: "start" }, [Autoplay({ delay: 3000 })]);
  const galleryPrev = useCallback(() => { if (galleryApi) galleryApi.scrollPrev(); }, [galleryApi]);
  const galleryNext = useCallback(() => { if (galleryApi) galleryApi.scrollNext(); }, [galleryApi]);

  const sectionMap: Record<string, React.ReactNode> = {
    hero: (
      <section key="hero" className="relative min-h-screen overflow-hidden">
        <div className="overflow-hidden h-screen" ref={heroRef}>
          <div className="flex h-full touch-pan-y">
            {heroSlides.map((slide, i) => (
              <div key={i} className="flex-[0_0_100%] min-w-0 relative h-screen flex items-center justify-center">
                <img src={slide.img} alt={slide.headline} className="absolute inset-0 w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-[80px]">
                  <div className="h-24 md:h-32" />
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary mb-10">
                    <Trophy size={14} />
                    <span className="text-xs font-display tracking-widest uppercase font-semibold">Eliada Sports Agency</span>
                  </motion.div>
                  <motion.h1 key={`h-${i}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-white tracking-tight mb-6">
                    {slide.headline} <br /><span className="text-primary">{slide.highlight}</span>
                  </motion.h1>
                  <motion.p key={`p-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }} className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-12 leading-relaxed">{slide.sub}</motion.p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link href="/join"><Button size="lg" className="w-full sm:w-auto gap-2 px-8">Join The Agency <ArrowRight size={18} /></Button></Link>
                    <Link href="/services"><Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">Explore Services</Button></Link>
                  </div>
                  <div className="h-36 md:h-48" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={heroPrev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors z-20 backdrop-blur-sm"><ChevronLeft size={24} /></button>
        <button onClick={heroNext} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors z-20 backdrop-blur-sm"><ChevronRight size={24} /></button>
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-3 z-20">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => heroScrollTo(i)} className={`h-2 rounded-full transition-all duration-300 ${i === heroIndex ? "bg-primary w-8" : "bg-white/50 w-2 hover:bg-white/80"}`} />
          ))}
        </div>
      </section>
    ),

    feature_strip: (
      <section key="feature_strip" className="bg-secondary border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            {featureStrip.map((item, i) => {
              const Icon = ICON_MAP[item.icon] ?? GraduationCap;
              return (
                <div key={i} className="flex items-start gap-4 p-8 group hover:bg-white/5 transition-colors">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Icon className="text-primary w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white text-base mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    ),

    stats: (
      <section key="stats" className="py-16 bg-background border-b border-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1, type: "spring" }} className="flex flex-col items-center justify-center p-4">
                <span className="text-5xl md:text-6xl font-display font-bold text-primary mb-2">{stat.num}</span>
                <span className="text-gray-400 font-medium uppercase tracking-widest text-sm">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    ),

    commitment: (
      <section key="commitment" className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-primary text-primary-foreground rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden shadow-[0_0_50px_rgba(255,191,0,0.15)]">
            <ShieldCheck className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-8">OUR COMMITMENT</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 font-display text-xl md:text-2xl font-semibold tracking-wide">
              {(commitmentMeta.pillars ?? []).map((p, i) => <div key={i}>{p}</div>)}
            </div>
            <p className="max-w-3xl mx-auto text-lg md:text-xl opacity-90 font-medium leading-relaxed">{commitmentMeta.description}</p>
          </div>
        </div>
      </section>

    ),

    mission: (
      <section key="mission" className="py-24 relative bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-gray-900">
                {(missionMeta.headline ?? "EMPOWERING ATHLETES TO REACH THEIR FULL POTENTIAL").split(" ").map((word, i) =>
                  word === "ATHLETES" ? <span key={i} className="text-primary"> {word} </span> : word + " "
                )}
              </h2>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">{missionText}</p>
              <ul className="space-y-4 mb-8">
                {(missionMeta.bullets ?? []).map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Target className="text-primary shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link href="/about"><Button className="gap-2">Read Our Story <ArrowRight size={16} /></Button></Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full z-0" />
              <img src={missionMeta.image} alt="Athletes celebrating" className="relative z-10 rounded-2xl shadow-2xl border border-white/10" />
            </div>
          </div>
        </div>
      </section>

    ),

    services_preview: (
      <section key="services_preview" className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">OUR CORE <span className="text-primary">EXPERTISE</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Comprehensive solutions for athlete development and career advancement.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesPreview.map((srv, i) => (
              <Card key={i} className="group border-white/5 hover:border-primary/50 transition-colors bg-background">
                <CardHeader><CardTitle className="group-hover:text-primary transition-colors">{srv.title}</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{srv.desc}</p>
                  {srv.href && <Link href={srv.href}><span className="text-primary text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">Learn More <ArrowRight size={14} /></span></Link>}
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center"><Link href="/services"><Button>View All Services</Button></Link></div>
        </div>
      </section>

    ),

    why_choose: (
      <section key="why_choose" className="py-24 relative overflow-hidden bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">WHY CHOOSE <span className="text-primary">ELIADA</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We stand out by putting athlete development and ethical representation first.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((feature, i) => {
              const Icon = ICON_MAP[feature.icon] ?? ShieldCheck;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-primary/50 shadow-sm hover:shadow-md transition-all">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6"><Icon className="text-primary w-7 h-7" /></div>
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

    ),

    global_presence: (
      <section key="global_presence" className="py-24 bg-background border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InteractiveWorldMap />
        </div>
      </section>
    ),

    core_values: (
      <section key="core_values" className="py-24 bg-secondary border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-6">
              <ShieldCheck size={14} /><span className="text-xs font-display tracking-widest uppercase font-semibold">What We Stand For</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {coreValuesHeadline.includes("CORE")
                ? <>{coreValuesHeadline.split(" ").slice(0, -1).join(" ")} <span className="text-primary">{coreValuesHeadline.split(" ").at(-1)}</span></>
                : coreValuesHeadline}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-background p-8 rounded-2xl border border-white/10 text-center hover:border-primary/40 transition-colors group">
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">{value.emoji}</div>
                <h3 className="font-display font-bold text-white text-2xl mb-3">{value.name}</h3>
                <p className="text-muted-foreground leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    ),

    athletes: showAthletesSection ? (
      <section key="athletes" className="py-24 bg-secondary border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-4">
                  <Star size={14} className="fill-primary" /><span className="text-xs font-display tracking-widest uppercase font-semibold">Our Roster</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-bold text-white">FEATURED <span className="text-primary">ATHLETES</span></h2>
                <p className="text-muted-foreground mt-3 max-w-xl">Meet some of the exceptional talents we represent and develop across the world stage.</p>
              </div>
              <Link href="/athletes"><Button variant="outline" className="gap-2 shrink-0">View All Athletes <ArrowRight size={16} /></Button></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredAthletes.map((athlete, i) => (
                <motion.div key={athlete.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="bg-[#0d1524] border border-white/10 rounded-2xl overflow-hidden hover:border-primary/40 transition-colors group">
                  <div className="relative">
                    {athlete.image_url ? (
                      <img src={athlete.image_url} alt={athlete.name} className="w-full h-56 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-56 bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-bold text-5xl">{athlete.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Star size={10} className="fill-white" /> Featured
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1524] via-transparent to-transparent" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-display font-bold text-white">{athlete.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                      <MapPin size={12} className="text-primary shrink-0" />
                      <span>{[athlete.position, athlete.sport, athlete.nationality].filter(Boolean).join(" · ")}</span>
                    </div>
                    {athlete.achievements.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {athlete.achievements.slice(0, 2).map((a, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary font-medium px-2.5 py-1 rounded-full">
                            <Trophy size={9} /> {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link href="/athletes"><Button className="gap-2">See Full Roster <ArrowRight size={16} /></Button></Link>
            </div>
          </div>
        </section>
    ) : null,

    blog: latestPosts.length > 0 ? (
      <section key="blog" className="py-24 bg-[#f8f7f4] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-4">
                <Rss size={14} /><span className="text-xs font-display tracking-widest uppercase font-semibold">News & Updates</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900">LATEST <span className="text-primary">NEWS</span></h2>
              <p className="text-gray-500 mt-3 max-w-xl">Stay up to date with the latest stories, athlete journeys, and agency news.</p>
            </div>
            <Link href="/blog"><Button variant="outline" className="gap-2 shrink-0 border-gray-300 text-gray-700 hover:bg-gray-100">View All Posts <ArrowRight size={16} /></Button></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {latestPosts.slice(0, 3).map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-primary/50 hover:shadow-md transition-all group"
              >
                {post.cover_image_url ? (
                  <div className="overflow-hidden aspect-[16/9]">
                    <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                    <Rss className="w-12 h-12 text-primary/30" />
                  </div>
                )}
                <div className="p-6">
                  {post.published_at && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
                      <Calendar size={12} />
                      {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  )}
                  <h3 className="font-display font-bold text-gray-900 text-lg mb-2 leading-snug group-hover:text-primary transition-colors line-clamp-2">{post.title}</h3>
                  {post.excerpt && <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>}
                  <Link href={`/blog/${post.slug}`}>
                    <span className="text-primary text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">Read More <ArrowRight size={14} /></span>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    ) : null,

    testimonials: (
      <section key="testimonials" className="py-24 bg-secondary border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">WHAT PEOPLE <span className="text-primary">SAY</span></h2>
          </div>
          <div className="relative">
            <div className="overflow-hidden" ref={testimonialsRef}>
              <div className="flex touch-pan-y">
                {testimonials.map((item, i) => (
                  <div key={i} className="flex-[0_0_100%] min-w-0 px-2">
                    <div className="bg-background rounded-3xl p-10 md:p-14 relative border border-white/5">
                      <Quote className="absolute top-8 left-8 text-primary/15 w-14 h-14" />
                      <p className="relative z-10 text-xl md:text-2xl italic text-gray-200 mb-10 pt-8 leading-relaxed text-center">"{item.quote}"</p>
                      <div className="text-center">
                        <div className="flex justify-center mb-4">
                          <img src={item.avatar} alt={item.author} className="w-16 h-16 rounded-full object-cover border-2 border-primary/40 shadow-lg shadow-primary/10" />
                        </div>
                        <p className="font-display font-bold text-primary tracking-wider text-lg">— {item.author}</p>
                        <p className="text-gray-500 text-sm mt-1">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={testPrev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-11 h-11 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors z-10 shadow-lg"><ChevronLeft size={20} /></button>
            <button onClick={testNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-11 h-11 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors z-10 shadow-lg"><ChevronRight size={20} /></button>
          </div>
        </div>
      </section>
    ),

    gallery_preview: (
      <section key="gallery_preview" className="py-24 bg-secondary/40 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-4">
                <Images size={14} /><span className="text-xs font-display tracking-widest uppercase font-semibold">Photo Gallery</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white">LIFE AT <span className="text-primary">ELIADA</span></h2>
              <p className="text-muted-foreground mt-3 max-w-xl">Moments from our academies, events, scouting missions, and athlete signings around the world.</p>
            </div>
            <Link href="/gallery"><Button variant="outline" className="gap-2 shrink-0">View Full Gallery <ArrowRight size={16} /></Button></Link>
          </div>
          <div className="relative overflow-hidden" ref={galleryRef}>
            <div className="flex touch-pan-y gap-4">
              {galleryPreview.map((img, i) => (
                <div key={i} className="flex-[0_0_80%] sm:flex-[0_0_45%] lg:flex-[0_0_30%] min-w-0 relative group">
                  <div className="relative overflow-hidden rounded-2xl border border-white/5 hover:border-primary/40 transition-colors aspect-[4/3]">
                    <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-white text-sm font-medium">{img.alt}</p>
                      <span className="text-primary text-xs font-display uppercase tracking-wider">{img.label}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-6">
            <button onClick={galleryPrev} className="w-10 h-10 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors shadow-lg"><ChevronLeft size={18} /></button>
            <button onClick={galleryNext} className="w-10 h-10 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-black transition-colors shadow-lg"><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>
    ),

    whatsapp: (
      <section key="whatsapp" className="py-20 bg-[#0a1a0f] border-t border-[#25D366]/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#25D366]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#25D366]/5 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center gap-10 md:gap-16"
          >
            {/* Text side */}
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#25D366]/10 border border-[#25D366]/20 mb-6">
                <svg viewBox="0 0 24 24" fill="#25D366" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.558 4.121 1.535 5.847L.057 23.804a.5.5 0 0 0 .61.65l6.196-1.625A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.805 9.805 0 0 1-5.031-1.385l-.36-.214-3.732.979.997-3.648-.235-.374A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
                <span className="text-[#25D366] text-xs font-display tracking-widest uppercase font-semibold">WhatsApp Channel</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4 leading-tight">
                Follow Us for <span className="text-[#25D366]">Daily Updates</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-md mx-auto md:mx-0">
                Stay informed with breaking transfer news, scouting reports, academy updates, and exclusive insights — straight to your WhatsApp.
              </p>
              <div className="flex flex-col sm:flex-row items-center md:items-start gap-4">
                <a
                  href="https://whatsapp.com/channel/0029Vb8V3Yc5q08ZB6ifBY3v"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bc5a] text-white font-bold px-7 py-3.5 rounded-xl transition-colors shadow-lg shadow-[#25D366]/20 text-sm"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.558 4.121 1.535 5.847L.057 23.804a.5.5 0 0 0 .61.65l6.196-1.625A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.805 9.805 0 0 1-5.031-1.385l-.36-.214-3.732.979.997-3.648-.235-.374A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
                  Follow Channel
                </a>
                <p className="text-gray-500 text-sm">or scan the QR code →</p>
              </div>
            </div>

            {/* QR code side */}
            <div className="shrink-0 flex flex-col items-center gap-4">
              <div className="bg-white p-4 rounded-2xl shadow-xl shadow-black/30">
                <img
                  src="/images/whatsapp-qr.png"
                  alt="WhatsApp Channel QR Code"
                  className="w-44 h-44 object-contain"
                />
              </div>
              <p className="text-gray-500 text-xs text-center">Scan to open in WhatsApp</p>
            </div>
          </motion.div>
        </div>
      </section>
    ),

    partner_preview: partnerPreviewEnabled ? (
      <section key="partner_preview" className="py-24 bg-background border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-5 py-2 mb-6">
              <Handshake size={15} className="text-primary" />
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Partnership Opportunities</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-white mb-4">
              {(partnerMeta.heading ?? "PARTNER WITH US").split(" ").map((w, i, arr) =>
                i === arr.length - 1
                  ? <span key={i} className="text-primary"> {w}</span>
                  : <span key={i}>{w} </span>
              )}
            </h2>
            <div className="h-1 w-20 bg-primary mx-auto rounded-full mb-6" />
            <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              {partnerMeta.subtext ?? "Connect your brand with the world's most exciting sports talent pipeline. Together, we develop champions — and winning partnerships."}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: Building2, title: "Title Sponsor", color: "from-yellow-500 to-amber-600", perks: ["Naming rights on key events", "Premium logo placement on kits", "Exclusive VIP access"] },
              { icon: Package, title: "Kit & Equipment", color: "from-blue-600 to-indigo-700", perks: ["Logo on training kits", "Equipment branding rights", "Social media mentions"] },
              { icon: Tv2, title: "Media Partner", color: "from-purple-600 to-pink-700", perks: ["Co-branded video content", "Broadcast sponsorship slots", "Shared digital audiences"] },
              { icon: GraduationCap, title: "Academy Partner", color: "from-emerald-600 to-teal-700", perks: ["Youth programme support", "Brand in training facilities", "Grassroots reach"] },
            ].map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300 group">
                <div className={`h-1.5 w-full bg-gradient-to-r ${tier.color}`} />
                <div className="p-6">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tier.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <tier.icon size={20} className="text-white" />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-4">{tier.title}</h3>
                  <ul className="space-y-2">
                    {tier.perks.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                        <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link href="/partner-with-us">
              <Button size="lg" className="gap-2 px-10">
                {partnerMeta.buttonText ?? "Explore All Partnerships"} <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    ) : null,

    cta: (
      <section key="cta" className="py-24 bg-secondary border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">{ctaMeta.heading ?? "READY TO START YOUR JOURNEY?"}</h2>
            <p className="text-xl text-muted-foreground mb-10 leading-relaxed">{ctaMeta.subtext}</p>
            <Link href="/join"><Button size="lg" className="gap-2 px-10">{ctaMeta.buttonText ?? "Join The Agency"} <ArrowRight size={18} /></Button></Link>
          </motion.div>
        </div>
      </section>
    ),
  };

  return (
    <>
      <SeoHead
        title="Eliada Sports Agency — Bridging Talent & Global Opportunity"
        description="Eliada Sports Agency connects elite athletes with clubs worldwide. Talent scouting, football academy, international transfers, player representation and career investment — all under one roof."
        path="/"
      />
      <PageTransition className="pb-0">
        {homeSectionOrder
          .filter(s => s.visible)
          .map(s => {
            if (s.id === "cta" && !homeSectionOrder.some(x => x.id === "whatsapp")) {
              return (
                <>
                  {sectionMap["whatsapp"]}
                  {sectionMap["cta"]}
                </>
              );
            }
            return sectionMap[s.id] ?? null;
          })}
        {!homeSectionOrder.some(s => s.id === "whatsapp") && !homeSectionOrder.some(s => s.id === "cta") && sectionMap["whatsapp"]}
      </PageTransition>
    </>
  );
}
